import React, { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import { useAuth } from '../../providers/AuthProvider';
import { attendanceService } from '../../services/attendanceService';

// Cache de session : une photo n'est téléchargée qu'une fois (URL objet, ou null si pas de photo),
// même en passant d'une séance à l'autre ou de la liste au trombi
const photoCache = new Map<number, Promise<string | null>>();

function loadPhoto(userId: number, getToken: () => Promise<string | null>): Promise<string | null> {
  let cached = photoCache.get(userId);
  if (!cached) {
    cached = (async () => {
      const token = await getToken();
      if (!token) return null;
      const blob = await attendanceService.getPhoto(userId, token);
      return blob ? URL.createObjectURL(blob) : null;
    })().catch(() => {
      photoCache.delete(userId); // erreur réseau : on réessaiera au prochain affichage
      return null;
    });
    photoCache.set(userId, cached);
  }
  return cached;
}

// Après un import de photos : oublier les « pas de photo » mémorisés
export function clearPhotoCache() {
  photoCache.forEach(promise => promise.then(url => url && URL.revokeObjectURL(url)));
  photoCache.clear();
}

interface Props {
  userId: number;
  lastname: string | null;
  firstname: string | null;
  size?: number;
  variant?: 'circular' | 'rounded' | 'square';
}

// Photo de trombinoscope, ou initiales si l'étudiant·e n'en a pas
const StudentPhoto: React.FC<Props> = ({ userId, lastname, firstname, size = 40, variant = 'circular' }) => {
  const { getValidToken } = useAuth();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    loadPhoto(userId, getValidToken).then(result => { if (active) setUrl(result); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const initials = `${(firstname || '').charAt(0)}${(lastname || '').charAt(0)}`.toUpperCase();
  return (
    <Avatar src={url || undefined} variant={variant} alt={`${firstname || ''} ${lastname || ''}`}
      sx={{ width: size, height: size, fontSize: size / 2.6 }}>
      {initials}
    </Avatar>
  );
};

export default StudentPhoto;
